import { Image, Modal, notification } from 'antd';
import html2canvas from 'html2canvas-pro';
import React, { useRef } from 'react';
import { XIcon } from 'src/assets/icons';
import { Button } from 'src/components/ui/button';
import { useAppTranslation } from 'src/i18n/hooks';
import { UserPointData } from 'src/services/leaderboard/get-user-points';
import { getTierNameByPoint } from '..';

export const getTierImageBgByPoint = (point: number) => {
  if (point > 500000) {
    return '/images/whale-shark-bg.png';
  } else if (point > 100000) {
    return '/images/great-white-bg.png';
  } else if (point > 10000) {
    return '/images/hammer-head-bg.png';
  } else if (point > 1000) {
    return '/images/sword-fish-bg.png';
  } else if (point > 100) {
    return '/images/clown-fish-bg.png';
  } else {
    return '/images/shrimp-bg2.png';
  }
};

interface Props {
  open: boolean;
  onClose: () => void;
  data?: UserPointData;
}

const SharePointModal: React.FC<Props> = ({ open, data, onClose }) => {
  const cardRef = useRef<HTMLDivElement>(null);

  const { t } = useAppTranslation(['leaderboard']);

  const handleShareAndTweet = async () => {
    if (cardRef.current) {
      const canvas = await html2canvas(cardRef.current);
      const imageData = canvas.toDataURL('image/png');

      // Convert to blob for sharing
      const blob = await (await fetch(imageData)).blob();

      // Check if device supports native sharing (mobile devices)
      const tweetText = `My total point on vdex is ${data?.points}`;
      if (
        navigator.share &&
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      ) {
        try {
          const file = new File([blob], 'vdex-position.png', { type: 'image/png' });
          await navigator.share({
            text: tweetText,
            files: [file],
          });
          return;
        } catch (shareError) {
          console.log('Native share failed, falling back to clipboard + Twitter');
        }
      }

      try {
        // Try to copy image to clipboard (modern browsers)
        if (navigator.clipboard && window.ClipboardItem) {
          const clipboardItem = new ClipboardItem({
            'image/png': blob,
          });
          await navigator.clipboard.write([clipboardItem]);

          // Show success notification
          notification.success({
            message: t('imageCopiedToClipboard', { ns: 'trade' }),
            description: t('pasteImageInTweet', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 15, //15s
          });
        } else {
          // Fallback: download for older browsers
          const downloadLink = document.createElement('a');
          downloadLink.href = imageData;
          downloadLink.download = 'vdex-point.png';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          notification.info({
            message: t('imageDownloaded', { ns: 'trade' }),
            description: t('attachImageToTweet', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 15, //15s
          });
        }
      } catch (clipboardError) {
        console.log('Clipboard failed, falling back to download');
        // Fallback to download
        const downloadLink = document.createElement('a');
        downloadLink.href = imageData;
        downloadLink.download = 'vdex-point.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        notification.info({
          message: t('imageDownloaded', { ns: 'trade' }),
          description: t('attachImageToTweet', { ns: 'trade' }),
          placement: 'bottomRight',
          duration: 15, //15s
        });
      }

      // Open Twitter with text
      setTimeout(() => {
        const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
        window.open(twitterUrl, '_blank');
      }, 1000);
    }
  };

  const copyImage = async () => {
    if (cardRef.current) {
      const canvas = await html2canvas(cardRef.current);
      const imageData = canvas.toDataURL('image/png');

      // Convert to blob for sharing
      const blob = await (await fetch(imageData)).blob();
      try {
        if (navigator.clipboard && window.ClipboardItem) {
          const clipboardItem = new ClipboardItem({
            'image/png': blob,
          });
          await navigator.clipboard.write([clipboardItem]);

          // Show success notification
          notification.success({
            message: t('imageCopiedToClipboard', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 5,
          });
        } else {
          // Fallback: download for older browsers
          const downloadLink = document.createElement('a');
          downloadLink.href = imageData;
          downloadLink.download = 'vdex-point.png';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);

          notification.success({
            message: t('browserNotSupportCopyImage', { ns: 'trade' }),
            placement: 'bottomRight',
            duration: 5, //5s
          });
        }
      } catch {
        // Fallback: download for older browsers
        const downloadLink = document.createElement('a');
        downloadLink.href = imageData;
        downloadLink.download = 'vdex-point.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        notification.success({
          message: t('browserNotSupportCopyImage', { ns: 'trade' }),
          placement: 'bottomRight',
          duration: 5, //5s
        });
      }
    }
  };

  const captureImage = async () => {
    if (cardRef.current) {
      try {
        const canvas = await html2canvas(cardRef.current);
        const imageData = canvas.toDataURL('image/png');

        const blob = await (await fetch(imageData)).blob();
        const blobUrl = URL.createObjectURL(blob);

        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = 'vdex-point.png';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        URL.revokeObjectURL(blobUrl);

        return imageData;
      } catch (error) {
        console.error('Error capturing image:', error);
        return null;
      }
    }
    return null;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      className="[&_.ant-modal-content]:p-[20px]"
      title={null}
      footer={null}
      closeIcon={null}
    >
      <div
        ref={cardRef}
        style={{
          backgroundImage: `url('${getTierImageBgByPoint(data?.points ?? 0)}')`,
        }}
        className="bg-cover pnl-card flex flex-col px-[20px] pb-[20px] pt-[15px] mobilePnl:pt-[25px] mobilePnl:pb-[0px] text-white shadow-l aspect-square bg-no-repeat"
      >
        <div className="flex justify-between h-full flex-col items-start">
          <div className="flex flex-col items-center w-full mt-[40px] xsm:mt-[60px]">
            <p className="text-base sm2:text-xl">{t('myTotalPointsOnVdex')}</p>
            <p className="text-2xl sm2:text-4xl font-bold">
              {Number(data?.points ?? 0).toLocaleString('en-US', {
                maximumFractionDigits: 4,
                minimumFractionDigits: 0,
              })}
            </p>
          </div>

          <div className="flex justify-center w-full mb-[5px] xsm:mb-[15px]  mobilePnl:mb-[30px]">
            <div className="rounded-[6px] px-[20px] py-[2px] bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] gap-[2px] text-white">
              <span>{getTierNameByPoint(data?.points ?? 0)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-[10px] mt-[16px]">
        <Button
          className="bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] hover:opacity-[0.9] gap-[2px] text-white"
          onClick={handleShareAndTweet}
        >
          <Image preview={false} width={18} height={18} src={XIcon} />
          <span>{t('tweet', { ns: 'trade' })}</span>
        </Button>
        <div className="flex gap-[10px]">
          <Button
            className="flex-1 p-[1px] rounded-[6px] bg-gradient-to-r from-[#0f1fd0] to-[#c403f6]  hover:bg-[#312f39] text-white"
            onClick={copyImage}
          >
            <div className="bg-black rounded-[6px] h-[100%] w-full hover:opacity-[0.9] flex items-center justify-center">
              {t('copyImage', { ns: 'trade' })}
            </div>
          </Button>
          <Button
            className="flex-1 p-[1px] rounded-[6px] bg-gradient-to-r from-[#0f1fd0] to-[#c403f6]  hover:bg-[#312f39] text-white"
            onClick={captureImage}
          >
            <div className="bg-black rounded-[6px] h-[100%] w-full hover:opacity-[0.9] flex items-center justify-center">
              {t('download', { ns: 'trade' })}
            </div>
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default SharePointModal;
